import {
  Component,
  QueryList,
  ViewChild,
  ViewChildren,
  Renderer2,
  Inject,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
} from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SearchRequest } from 'src/app/models/requests/searchRequest';
import { ExpansionPanelComponent } from 'src/app/shared/components/expansion-panel/expansion-panel.component';
import { SearchFieldComponent } from 'src/app/shared/components/search-field/search-field.component';
import { DataService } from 'src/app/shared/services/data.service';
import { UserAgentService } from 'src/app/shared/services/user-agent.service';
import { RouterService } from 'src/app/shared/services/router.service';
import { environment } from 'src/environments/environment';
import { BreakpointObserver } from '@angular/cdk/layout';
import { DOCUMENT } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { FullscreenComponent } from 'src/app/shared/additional-styles/fullscreen-component';
import { FavoritesService } from 'src/app/shared/services/favorites.service';
import {CatalogCard} from '../../shared/interfaces/catalog-card';

declare const favoritesHandler: any;

@Component({
  selector: 'app-catalog-page',
  templateUrl: './catalog-page.component.html',
  styleUrls: [
    './catalog-page.component.scss',
    './catalog-page.component.en.scss',
  ],
})
export class CatalogPageComponent
  extends FullscreenComponent
  implements OnInit, OnDestroy
{
  @Input() pageUrl: string | null = null;
  @Input() childNodeId: string | unknown = null;
  // For checking if some filtered was written
  @ViewChildren(ExpansionPanelComponent)
  expansionComponents: QueryList<ExpansionPanelComponent> =
    new QueryList<ExpansionPanelComponent>();
  // For scrolling when page was changed
  @ViewChild(SearchFieldComponent) searchFieldComponent?: SearchFieldComponent;
  // Current page data
  catalogPageData: any;
  currentPage: number = 1;
  filters?: string;
  departmentsFilters?: string;
  facultiesFilters?: string;
  term: string = '';
  isMobile: boolean = false;
  showFiltersMobileModal: boolean = false;
  isLoading: boolean = false;
  private ngUnsubscribeUrlListener: Subject<void> = new Subject<void>();
  allCards: any[] = []; 
  showFavoritesOnly: boolean = false;

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dataService: DataService,
    private routerService: RouterService,
    private favoritesService: FavoritesService,
    renderer: Renderer2,
    @Inject(DOCUMENT) override document: Document,
    private userAgentService: UserAgentService
  ) {
    super(renderer, document);
    this.isMobile = this.userAgentService.isMobileDevice();
  }

  ngOnInit() {
    this.changeAddithinalStyles();
    // handle url parameters on init
    this.handelUrlParameters();
  }

  ngOnDestroy(): void {
    this.changeAddithinalStyles(true);
    this.ngUnsubscribeUrlListener.next();
    this.ngUnsubscribeUrlListener.complete();
  }

  private changeAddithinalStyles(destroy: boolean = false) {
    let element = this.document.querySelector('.page-content');
    if (element) {
      if (destroy) {
        this.renderer.removeStyle(element, 'padding');
      } else {
        this.renderer.setStyle(element, 'padding', 'inherit');
      }
    }

    super.setAddithonalStyles(destroy);

    element = this.document.querySelector(
      '.page-content app-standard-content-page app-card-header'
    );
    if (element) {
      if (destroy) {
        this.renderer.removeStyle(element, 'display');
      } else {
        this.renderer.setStyle(element, 'display', 'none');
      }
    }

    element = this.document.querySelector('.portal-body .page-content-wrap');
    if (element) {
      if (destroy) {
        this.renderer.removeStyle(element, 'background-color');
      } else {
        this.renderer.setStyle(element, 'background-color', 'white');
      }
    }
  }

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    let width = (event.target as Window).innerWidth;
    this.isMobile = width <= 1300;
  }

  onFilterStatusChange(checked: boolean, filter: any) {
    filter.selected = checked;

    if (filter.isDepartmentsFilter || filter.isFacultiesFilter) {
      if (filter.isDepartmentsFilter) {
        this.departmentsFilters = '';
        let selectedFilterGroups: string[] = [];
        this.catalogPageData.filters.forEach((filterGroup: any) => {
          let selectedFilters = filterGroup.filters.filter(
            (f: any) => f.isDepartmentsFilter && f.selected
          );
          if (selectedFilters.length > 0) {
            selectedFilters
              .map((filter: any) => filter.filterKey)
              .forEach((key: any) => selectedFilterGroups.push(key));
          }
        });
        if (selectedFilterGroups.length > 0)
          this.departmentsFilters = selectedFilterGroups.join(',');
      }

      if (filter.isFacultiesFilter) {
        this.facultiesFilters = '';
        let selectedFilterGroups: string[] = [];
        this.catalogPageData.filters.forEach((filterGroup: any) => {
          let selectedFilters = filterGroup.filters.filter(
            (f: any) => f.isFacultiesFilter && f.selected
          );
          if (selectedFilters.length > 0) {
            selectedFilters
              .map((filter: any) => filter.filterKey)
              .forEach((key: any) => selectedFilterGroups.push(key));
          }
        });
        if (selectedFilterGroups.length > 0)
          this.facultiesFilters = selectedFilterGroups.join(',');
      }
    } else {
      this.filters = '';
      let selectedFilterGroups: string[] = [];

      this.catalogPageData.filters.forEach((filterGroup: any) => {
        let selectedFilters = filterGroup.filters.filter((f: any) => {
          // if filter name was written in search bar -> delete search query from params
          if (f.filterDisplayText === this.term) this.onSearchInputChange('');
          return !f.isDepartmentsFilter && !f.isFacultiesFilter && f.selected;
        });
        if (selectedFilters.length > 0) {
          selectedFilters
            .map((filter: any) => filter.filterKey)
            .forEach((key: any) => selectedFilterGroups.push(key));
        }
      });
      if (selectedFilterGroups.length > 0)
        this.filters = selectedFilterGroups.join(',');
    }

    this.currentPage = 1;
    this.updateUrlParameters();
    this.getSearchResult();
  }

  onPageChange(pageNumber: number): void {
    this.currentPage = pageNumber;
    this.searchFieldComponent?.scrollToSearchForm();
    this.updateUrlParameters();
    this.getSearchResult();
  }

  onSearchInputChange(searchQuery: string) {
    this.term = searchQuery;
    this.updateUrlParameters();
  }

  onAutoselectInputSubmit(value?: string) {
    if (value) {
      this.onSearchInputChange('');
      this.expansionComponents.forEach((c) => {
        c.checkMatchedFilterWithPersonalizationFilters(value);
      });
      return;
    }

    this.currentPage = 1;
    this.getSearchResult();
  }

  onSearchInputSubmit() {
    this.currentPage = 1;
    this.getSearchResult();
  }

  private getPageData() {
    this.isLoading = true;
    let url = environment.apiUrl + '/Portal/Catalog-Api/Index';
    const searchRequest: SearchRequest = {
      nodeId: this.childNodeId
        ? parseInt(this.childNodeId.toString())
        : undefined,
      pageUrl:
        this.pageUrl?.replace('/private/en/', '') ??
        this.routerService.getCurrentComponentRoute(),
      cultureCode: this.dataService.getCultureCode(),
      term: this.term,
      filters: this.filters,
      pageNumber: this.currentPage,
      departmentsFilters: this.departmentsFilters,
      facultiesFilters: this.facultiesFilters,
    };

    this.dataService.getPortalPageData(url, searchRequest).subscribe({
      next: (data) => {
        const pageData = JSON.parse(data);
        pageData.searchResults.cards = this.addIsFavoritePropertyToCard(
          pageData.searchResults.cards
        );
        this.catalogPageData = pageData;

        // Normalize: ensure searchResults exists
       

        this.allCards = Array.isArray(this.catalogPageData.searchResults?.cards)
          ? this.catalogPageData.searchResults?.cards
          : [];

        if (this.catalogPageData.filters.length) {
          if (this.filters) {
            let selecteFilters = this.filters.split(',').map(Number);
            this.catalogPageData.filters.forEach((c: any) => {
              if (c.filters) {
                c.filters.forEach((x: any) => {
                  if (
                    !x.isDepartmentsFilter &&
                    !x.isFacultiesFilter &&
                    selecteFilters.includes(x.filterKey)
                  ) {
                    x.selected = true;
                  }
                });
              }
            });
          }

          if (this.departmentsFilters) {
            let selecteFilters = this.departmentsFilters.split(',').map(Number);
            this.catalogPageData.filters.forEach((c: any) => {
              if (c.filters) {
                c.filters.forEach((x: any) => {
                  if (
                    x.isDepartmentsFilter &&
                    selecteFilters.includes(x.filterKey)
                  ) {
                    x.selected = true;
                  }
                });
              }
            });
          }

          if (this.facultiesFilters) {
            let selecteFilters = this.facultiesFilters.split(',').map(Number);
            this.catalogPageData.filters
              .filter((f: any) => f.isFacultiesFilter)
              ?.forEach((c: any) => {
                if (c.filters) {
                  c.filters.forEach((x: any) => {
                    if (
                      x.isFacultiesFilter &&
                      selecteFilters.includes(x.filterKey)
                    ) {
                      x.selected = true;
                    }
                  });
                }
              });
          }
        }

        
        this.isLoading = false;
      },
      error: (e) => console.error(e),
    });
  }

  private handelUrlParameters() {
    this.route.queryParams
      .pipe(takeUntil(this.ngUnsubscribeUrlListener))
      .subscribe({
        next: (params) => {
          let page = params['page'];
          this.filters = params['filters'];
          this.term = params['term'];
          this.departmentsFilters = params['departmentsFilters'];
          this.facultiesFilters = params['facultiesFilters'];

          let validPage = page && parseInt(page) >= 1;
          this.currentPage = validPage ? parseInt(page) : 1;

          this.getPageData();
        },
        error: (e) => console.error('Error while fetching query params', e),
      });
  }

  // Sending request to server to get filtered cards
  private getSearchResult() {
    this.isLoading = true;
    const searchRequest: SearchRequest = {
      nodeId: this.catalogPageData.nodeId,
      childNodeId: this.childNodeId
        ? parseInt(this.childNodeId.toString())
        : undefined,
      pageUrl: this.pageUrl ?? this.routerService.getCurrentComponentRoute(),
      cultureCode: this.dataService.getCultureCode(),
      term: this.term,
      filters: this.filters,
      pageNumber: this.currentPage,
      departmentsFilters: this.departmentsFilters,
      facultiesFilters: this.facultiesFilters,
    };

     if (this.showFavoritesOnly) {
     
      const favIdsStr = this.favoritesService.getFavoritesForCatalog();
      const favIds = Array.isArray(favIdsStr)
        ? favIdsStr.map((id) => parseInt(`${id}`, 10)).filter((n) => !isNaN(n))
        : [];

      searchRequest.favoriteIds = favIds;
    }

    this.dataService
      .getPortalCardSearchResult(
        environment.apiUrl?.replace('/private/en/', '') +
          '/Portal/Catalog-Api/GetPortalSearchResult',
        searchRequest
      )
      .subscribe({
        next: (res) => {
          res = JSON.parse(res);
          this.allCards = Array.isArray(res.cards) ? [...this.addIsFavoritePropertyToCard(res.cards)] : [];

          const searchResults =
            this.catalogPageData?.searchResults ?? this.catalogPageData;
          const serverCount =
            res.totalSearchResultsCount ?? this.allCards.length;
          searchResults.totalSearchResultsCount = serverCount;
          searchResults.cards = [...this.allCards];

          this.isLoading = false;
        },
        error: (e) => {
          console.error(e);
          this.isLoading = false;
        },
      });
  }

  // Updating url parameters
  private updateUrlParameters() {
    let queryParams: any = {};

    if (this.currentPage > 1) queryParams.page = this.currentPage;

    if (this.term != undefined && this.term !== '')
      queryParams.term = this.term;

    if (this.filters != undefined && this.filters !== '') {
      queryParams.filters = this.filters;
    }

    if (
      this.departmentsFilters != undefined &&
      this.departmentsFilters !== ''
    ) {
      queryParams.departmentsFilters = this.departmentsFilters;
    }

    if (this.facultiesFilters != undefined && this.facultiesFilters !== '') {
      queryParams.facultiesFilters = this.facultiesFilters;
    }

    queryParams = Object.keys(queryParams).length > 0 ? queryParams : null;

    var url = this.router
      .createUrlTree([], {
        relativeTo: this.route,
        queryParams: queryParams,
      })
      .toString();

    window.history.pushState({ path: url }, '', url);
  }

  openFiltersMobilePopup() {
    this.showFiltersMobileModal = true;
    this.changeSidebarMenu(false);
  }

  closeFiltersMobilePopup() {
    this.showFiltersMobileModal = false;
    this.changeSidebarMenu(true);
  }

  changeSidebarMenu(show: boolean) {
    let element = this.document.querySelector('app-sidebar');
    if (!element) return;
    if (show) {
      this.renderer.removeStyle(element, 'display');
    } else {
      this.renderer.setStyle(element, 'display', 'none');
    }
  }

  getFiltersEnableStatus(): boolean {
    return !(this.filters != undefined && this.filters != '');
  }

  clearFilters(event?: any) {
    if (event) event.preventDefault();

    if (this.filters && this.catalogPageData.filters.length) {
      this.catalogPageData.filters.forEach((c: any) => {
        if (c.filters) {
          c.filters.forEach((x: any) => {
            x.selected = false;
          });
        }
      });
    }

    this.filters = undefined;
    this.departmentsFilters = undefined;
    this.facultiesFilters = undefined;

    this.expansionComponents.forEach((c) => {
      c.clearFilter();
    });

    this.currentPage = 1;
    this.updateUrlParameters();
    this.getSearchResult();
  }

  clearAllParams() {
    this.term = '';
    this.searchFieldComponent?.clearInput();
    this.clearFilters();
  }

  favoritesFilterCheckBoxChanged(event?: any) {
  
    this.showFavoritesOnly = event.target.checked;
    this.currentPage = 1;
    this.updateUrlParameters();
    this.getSearchResult();
  }

  // when clicking favorite button inside card
  favoriteSave(nodeId: any) {
      const isFav = this.favoritesService.toggleFavorite(nodeId);

    this.catalogPageData.searchResults.cards =
      this.catalogPageData.searchResults.cards.map((card: CatalogCard) => {
        if (card.nodeId === nodeId) {
          return { ...card, isFavorite: isFav };
        }
        return card;
      });
  }

  isFavorite(id: any) {  
    return this.favoritesService.isFavorite(id);
  }

  private addIsFavoritePropertyToCard(cards: any[]): any[] {
  return cards.map(c => ({
    ...c,
    isFavorite: this.favoritesService.isFavorite(c.nodeId)
  }));
}

  
}
